import { Alert, Button, Card, Col, DatePicker, Empty, Grid, Pagination, Progress, Row, Space, Spin, Statistic, Table, Tag, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, ReloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { brand } from "../app/theme.ts";
import { SCHOOL_TIME_ZONE } from "../app/time.ts";

type Movement = components["schemas"]["StudentMovement"];
const MOVEMENTS_PER_PAGE = 10;

export function DashboardPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const [month, setMonth] = useState<string>();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ["school-dashboard", month, page],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET("/reports/school-dashboard", {
        params: { query: { ...(month ? { Month: month } : {}), Page: page, PageSize: MOVEMENTS_PER_PAGE } }, signal,
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
  const data = query.data;
  const today = dayjs(data?.today ?? dayjs().tz(SCHOOL_TIME_ZONE).format("YYYY-MM-DD"));
  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div>
        <Typography.Title level={2} style={{ margin: 0 }}>{t("dashboard.title")}</Typography.Title>
        <Typography.Text type="secondary">{t("dashboard.asOf", { date: today.format("DD.MM.YYYY") })}</Typography.Text>
      </div>
      <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => void query.refetch()}>{t("dashboard.refresh")}</Button>
    </div>
  );

  if (query.isError) return <Space orientation="vertical" style={{ width: "100%" }} size="large">{header}<Alert type="error" showIcon title={t("dashboard.loadError")} /></Space>;
  if (!data) return <Space orientation="vertical" style={{ width: "100%" }} size="large">{header}<Spin description={t("app.loading")}><div style={{ minHeight: 240 }} /></Spin></Space>;

  const counts = data.counts;
  const active = Number(counts.active);
  const net = Number(counts.joined) - Number(counts.left);
  const metrics = [
    { label: t("dashboard.active"), value: counts.active },
    { label: t("dashboard.classes"), value: counts.classes },
    { label: t("dashboard.teachers"), value: counts.teachers },
    { label: t("dashboard.unplaced"), value: counts.unplaced },
  ];
  const living = [
    { label: t("dashboard.boarding"), value: Number(counts.boarding), color: brand.green },
    { label: t("dashboard.day"), value: Number(counts.day), color: "#648b7f" },
    { label: t("dashboard.livingUnknown"), value: Number(counts.livingUnknown), color: "#b7791f" },
  ];

  return (
    <Space orientation="vertical" size={24} style={{ width: "100%" }}>
      {header}
      <Row gutter={[16, 16]}>
        {metrics.map((metric) => <Col xs={12} lg={6} key={metric.label}><Card size="small" style={{ height: "100%" }}><Statistic title={metric.label} value={metric.value} /></Card></Col>)}
      </Row>
      <Row gutter={[24, 24]}>
        <Col xs={24} xl={18}>
          <Card>
            <Space orientation="vertical" size={20} style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>{t("dashboard.movements")}</Typography.Title>
                  <Typography.Text type="secondary">{t("dashboard.period", { from: dayjs(data.from).format("DD.MM.YYYY"), to: dayjs(data.to).format("DD.MM.YYYY") })}</Typography.Text>
                </div>
                <DatePicker picker="month" allowClear={false} value={dayjs(month ?? data.from)} format="MMMM YYYY"
                  aria-label={t("dashboard.month")} disabledDate={(date) => date.isAfter(today, "month")}
                  onChange={(date) => { if (date) { setMonth(date.startOf("month").format("YYYY-MM-DD")); setPage(1); } }} />
              </div>
              <Row gutter={[16, 16]}>
                <Col xs={8}><Statistic title={t("dashboard.joined")} value={counts.joined} prefix={<ArrowUpOutlined style={{ color: "#2f7d4f" }} />} /></Col>
                <Col xs={8}><Statistic title={t("dashboard.left")} value={counts.left} prefix={<ArrowDownOutlined style={{ color: "#c2412d" }} />} /></Col>
                <Col xs={8}><Statistic title={t("dashboard.net")} value={net} prefix={net > 0 ? "+" : undefined} /></Col>
              </Row>
              {screens.md ? <Table<Movement> size="small" rowKey={(row) => `${row.kind}-${row.id}`} dataSource={data.movements.items} loading={query.isFetching}
                scroll={{ x: 640 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("dashboard.noMovements")} /> }}
                pagination={{ current: page, pageSize: MOVEMENTS_PER_PAGE, total: Number(data.movements.totalCount), showSizeChanger: false, onChange: setPage, size: "small", simple: !screens.md, hideOnSinglePage: true }}
                columns={[
                  { title: t("dashboard.student"), dataIndex: "name", render: (name: string, row) => <Link to={`/students/${row.studentId}`}>{name}</Link> },
                  { title: t("dashboard.event"), dataIndex: "kind", render: (kind: string) => <Tag color={kind === "Joined" ? "success" : "error"}>{t(kind === "Joined" ? "dashboard.joined" : "dashboard.left")}</Tag> },
                  { title: t("dashboard.date"), dataIndex: "date", render: (date: string) => dayjs(date).format("DD.MM.YYYY") },
                  { title: t("dashboard.classCourse"), render: (_, row) => <div>{row.className ?? t("dashboard.notAssigned")}<br /><Typography.Text type="secondary">{row.courseName}</Typography.Text></div> },
                  { title: t("dashboard.reason"), dataIndex: "reason", render: (reason: string | null) => reason ?? t("dashboard.notApplicable") },
                ]} /> : <Spin spinning={query.isFetching}>
                {data.movements.items.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("dashboard.noMovements")} /> :
                  <ul aria-label={t("dashboard.movements")} style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {data.movements.items.map((row) => <li key={`${row.kind}-${row.id}`} style={{ paddingBlock: 16, borderBottom: `1px solid ${brand.border}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                        <Link to={`/students/${row.studentId}`}>{row.name}</Link>
                        <Tag color={row.kind === "Joined" ? "success" : "error"}>{t(row.kind === "Joined" ? "dashboard.joined" : "dashboard.left")}</Tag>
                      </div>
                      <Typography.Paragraph type="secondary" style={{ marginBlock: "4px 0", fontSize: 12 }}>{dayjs(row.date).format("DD.MM.YYYY")} — {row.className ?? t("dashboard.notAssigned")}{row.courseName ? ` / ${row.courseName}` : ""}</Typography.Paragraph>
                      {row.reason && <Typography.Paragraph style={{ marginBlock: "4px 0" }}>{row.reason}</Typography.Paragraph>}
                    </li>)}
                  </ul>}
                <Pagination current={page} pageSize={MOVEMENTS_PER_PAGE} total={Number(data.movements.totalCount)} onChange={setPage}
                  simple hideOnSinglePage showSizeChanger={false} size="small" style={{ marginTop: 16 }} />
              </Spin>}
              <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>{t("dashboard.movementHint")}</Typography.Paragraph>
            </Space>
          </Card>
        </Col>
        <Col xs={24} xl={6}>
          <Space orientation="vertical" size={24} style={{ width: "100%" }}>
            <Card title={t("dashboard.living")}>
              <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                {living.map((item) => <div key={item.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><Typography.Text>{item.label}</Typography.Text><Typography.Text strong>{item.value}</Typography.Text></div>
                  <Progress percent={active ? item.value / active * 100 : 0} showInfo={false} strokeColor={item.color} size="small" />
                </div>)}
                <Typography.Text type="secondary">{t("dashboard.frozen", { count: Number(counts.frozen) })}</Typography.Text>
              </Space>
            </Card>
            <Card title={t("dashboard.quickLinks")}>
              <Space orientation="vertical" size={12}>
                <Link to="/families">{t("nav.families")}</Link>
                {hasPermission("academic.read") && <Link to="/academic/classes">{t("nav.classes")}</Link>}
                <Link to="/teachers">{t("nav.teachers")}</Link>
                {hasPermission("reports.read") && <Link to="/reports/attendance">{t("nav.attendanceReport")}</Link>}
                {hasPermission("billing.read") && <Link to="/billing/rules">{t("nav.billingRules")}</Link>}
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>
    </Space>
  );
}
